import React from "react"
import DateTimeField from "components/Common/DateTimeField"
import { Alert, Button, Col, FormFeedback, FormGroup, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"
import { colourName, familyName, gameTypeName, joinNames, supplierNames, unitName } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { COLOURS, FAMILIES, GAME_TYPES, GAME_TYPE_RULES, NAME_LIMIT, PACKAGE_NAME_LIMIT, PLACES_LIMIT, QUANTITY_RULES, RULES, SPORTS, followsColours,
  groupsRule, isTenis10Form, unitsOf } from "./productForm"
import { sportName } from "./productLabels"

// A Tenis10 medal counts places 1–4, each with its own stock (the canvas verdict C-A-medals); an adult one keeps its words.
const placeChoice = (count, tenis10) => ({ 1: "1 · Champion", 2: "2 · Champion and finalist" })[count] || (tenis10 ? `${count} · Places 1–${count}` : `${count}`)
// The note under a Tenis10 product's colours (the canvas verdict C-A, round 7 „A · bife blocate”): a ball or a lanyard follows the
// colours ticked, none for one added only by hand; every other family counts every competition, its colours ticked and locked.
const colourNote = form => {
  if (followsColours(form)) return form.tenis10Levels.length ? "The product follows the competitions of these colours. Galben = U12, U14 and Open."
    : "No colour: the product never fits a colour line, so it reaches a sheet only through „Add product” — never by itself, not even when a colour runs out of stock."
  return { medals: "Every colour: the same medals for every level; the lanyards carry the colour.",
    diplomas: "Every colour: one diploma per child, whatever the level." }[form.family]
    || "Every colour: the product counts every competition, whatever its colour."
}
// The rule's hint: R8 is the minimum to run the event, a Tenis10 lanyard's R5 counts its colours' podiums.
const ruleHint = form => form.ruleType === "perGroup" ? "The minimum to run the event: one group plays on one court. Rounded up to a whole unit, after the reserve."
  : form.ruleType === "perCompetition" && form.family === "lanyards" && isTenis10Form(form)
    ? "Counts the competitions of the product’s colours only: 4 lanyards per podium (places 1–4)." : "Rounded up to a whole unit, after the reserve."

// The options of a list: the settings' choices, then those the product keeps that the settings no longer list.
const withKept = (options, kept) => [...options, ...kept.filter(key => !options.some(option => option.key === key)).map(key => ({ key, name: key }))]
const options = (entries) => entries.map(([value, label]) => <option key={value} value={value}>{label}</option>)

function Field({ id, label, error, hint, children }) {
  return <FormGroup>
    <Label for={id}>{label}</Label>
    {children}
    {error && <FormFeedback>{error}</FormFeedback>}
    {hint && <FormText>{hint}</FormText>}
  </FormGroup>
}

function Check({ id, label, checked, disabled, onChange, children }) {
  return <FormGroup check className="mb-1">
    <Input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} />
    <Label check for={id}>{label}</Label>
    {children}
  </FormGroup>
}

// A product in the five sections of the catalogue prototype: names, scope, units, supply and stock,
// calculation; then its status. `editing` holds the form's values and the errors of its last check.
export default function ProductDialog({ editing, products, settings = {}, pending, failure, onChange, onSave, onClose }) {
  const { productId, name, form, errors = {} } = editing
  const close = pending ? undefined : onClose
  const set = field => value => onChange({ ...form, [field]: value })
  // The input of a form field, `product-<field>`, with its error; a select receives its options as children.
  // a day is the CP's day picker (P24)
  const input = (field, props = {}, children) => props.type === "date" ? <DateTimeField id={`product-${field}`} mode="date" value={form[field]} invalid={Boolean(errors[field])}
    onChange={event => set(field)(event.target.value)} {...props} /> : <Input id={`product-${field}`} value={form[field]} invalid={Boolean(errors[field])}
    onChange={event => set(field)(event.target.value)} {...props}>{children}</Input>
  const field = (key, label, props, children, extra = {}) => <Field id={`product-${key}`} label={label} error={errors[key]} {...extra}>
    {input(key, props, children)}</Field>
  const toggle = (key, value) => checked => set(key)(checked ? [...form[key], value] : form[key].filter(current => current !== value))
  const categories = withKept(settings.categories || [], form.categories)
  const balls = withKept((settings.balls || []).map(ball => ({ key: ball.name, name: ball.name })), form.officialBalls)
  // The other active products that cover a ball already, such as an older batch, named next to that ball.
  const coveredBy = ball => products.filter(product => product.id !== productId && product.active && product.officialBalls?.includes(ball))
    .map(product => product.name)
  // R3 refers to an active product with a rule of its own that is not a percentage; the one chosen stays offered.
  const references = products.filter(product => product.id !== productId && product.rule && product.rule.type !== "percentOf"
    && (product.active || product.id === form.reference))
  const select = { type: "select" }
  const tenis10 = isTenis10Form(form)
  const units = unitsOf(form)

  return <Modal isOpen size="lg" centered toggle={close} labelledBy="logistics-product-title" className="logistics-product-dialog">
    <ModalHeader id="logistics-product-title" toggle={close}>{productId ? `Edit product · ${name}` : "Add product"}</ModalHeader>
    <ModalBody>
      <h6 className="logistics-form-section">Names</h6>
      <Row>
        <Col md={6}>{field("name", "Name (base)", { maxLength: NAME_LIMIT })}</Col>
        <Col md={3}>{field("en", "English (optional)", { maxLength: NAME_LIMIT })}</Col>
        <Col md={3}>{field("bg", "Bulgarian (optional)", { maxLength: NAME_LIMIT })}</Col>
      </Row>
      <FormText className="mt-n2 mb-2 d-block">Shown in SportyaOS, on the printed sheet and in emails; the base name is used when a translation is missing.</FormText>

      <h6 className="logistics-form-section">Scope</h6>
      <Row>
        <Col md={6}>{field("family", "Family", select, options(FAMILIES.map(family => [family, familyName(family)])))}</Col>
        <Col md={6}>{field("sport", "Sport", select, options([["all", "All sports"], ...SPORTS.map(sport => [sport, sportName(sport)])]), {
          hint: tenis10 ? "Tenis10: the product reaches only Tenis10 events, whatever their category. „All sports” products reach them only without categories (the water)." : undefined })}</Col>
      </Row>
      {/* A Tenis10 product: the colours it follows instead of the event categories and the official balls (Events ADR 0016). */}
      {tenis10 ? <FormGroup tag="fieldset">
        <legend className="col-form-label pt-0">Tenis10 colours</legend>
        {COLOURS.map(level => <FormGroup key={level} check inline>
          <Input id={`product-colour-${level}`} type="checkbox" checked={!followsColours(form) || form.tenis10Levels.includes(level)} disabled={!followsColours(form)}
            onChange={event => toggle("tenis10Levels", level)(event.target.checked)} />
          <Label check for={`product-colour-${level}`}>{colourName(level)}</Label>
        </FormGroup>)}
        <FormText className="d-block">{colourNote(form)}</FormText>
      </FormGroup> : <FormGroup tag="fieldset">
        <legend className="col-form-label pt-0">Event categories</legend>
        <Check id="product-all-categories" label="All categories" checked={form.allCategories} onChange={set("allCategories")} />
        {!form.allCategories && <div className="logistics-form-choices">{categories.map(category =>
          <Check key={category.key} id={`product-category-${category.key}`} label={category.name} checked={form.categories.includes(category.key)}
            onChange={toggle("categories", category.key)} />)}</div>}
        {errors.categories && <div className="invalid-feedback d-block">{errors.categories}</div>}
      </FormGroup>}
      {form.family === "balls" && !tenis10 && <FormGroup tag="fieldset">
        <legend className="col-form-label pt-0">Official balls covered</legend>
        <div className="logistics-form-choices">{balls.map(ball => {
          const others = coveredBy(ball.key)
          return <Check key={ball.key} id={`product-ball-${ball.key}`} label={ball.name} checked={form.officialBalls.includes(ball.key)}
            onChange={toggle("officialBalls", ball.key)}>{others.length > 0 && <small className="text-muted d-block">also covered by {joinNames(others)}</small>}</Check>
        })}</div>
        <FormText>Several active products may cover the same ball, such as two batches: a new sheet takes the oldest that still has
          stock available, and a sheet keeps its own. A ball without one stays with the club, to check.</FormText>
      </FormGroup>}

      <h6 className="logistics-form-section">Units</h6>
      <Row>
        <Col sm={6} md={3}>{field("unit", "Unit on the sheet", select, options([...units.map(unit => [unit, unitName(unit)]),
          ...(units.includes(form.unit) ? [] : [[form.unit, `${unitName(form.unit)} (no longer used)`]])]))}</Col>
        <Col sm={6} md={3}>{field("perUnit", "Pieces per unit", { type: "number", min: 1, step: 1 })}</Col>
        <Col sm={6} md={3}>{field("unitWeight", "Weight per unit (kg)", { type: "number", min: 0, step: "0.01" })}</Col>
      </Row>
      <Row>
        <Col sm={6} md={3}>{field("groupingName", "Grouping package", { maxLength: PACKAGE_NAME_LIMIT, placeholder: "e.g. case" })}</Col>
        <Col sm={6} md={3}>{field("groupingUnits", "Units per package", { type: "number", min: 2, step: 1, placeholder: "e.g. 18" })}</Col>
      </Row>

      <h6 className="logistics-form-section">Supply and stock</h6>
      <Row>
        <Col sm={6} md={4}>{field("supplier", "Default supplier", select, options(Object.entries(supplierNames)))}</Col>
        <Col sm={6} md={4}>{field("minimum", "Minimum threshold", { type: "number", min: 0, step: 1 })}</Col>
        <Col sm={6} md={4}>{field("expiresOn", "Expiry date", { type: "date" })}</Col>
      </Row>
      <FormText className="mt-n2 mb-2 d-block">Every product is held in stock; whether its goods wait at Câmpina or at a supplier is up to
        the warehouse.</FormText>
      {form.family === "medals" && <FormText className="mt-n2 mb-2 d-block">Medals have one variant per place of their rule (Champion, Finalist),
        each with its own stock.</FormText>}

      <h6 className="logistics-form-section">Calculation</h6>
      <Row>
        <Col md={6}>{field("ruleType", "Rule", select, options([["", "No rule (manual quantity)"],
          ...Object.entries(RULES).filter(([type]) => type !== "perGroup" || groupsRule(form) || form.ruleType === type)]),
          { hint: ruleHint(form) })}</Col>
        {form.ruleType && <Col md={3}>{field("reserve", "Reserve (%)", { type: "number", min: 0, max: 100, step: 1 })}</Col>}
      </Row>
      <Row>
        {form.ruleType === "perPlace" && <Col md={6}>{field("places", "Places from 1 to", select,
          options(Array.from({ length: PLACES_LIMIT }, (_, index) => [index + 1, placeChoice(index + 1, tenis10)])),
          { hint: tenis10 ? `A medal product per place, each with its own stock (Place ${Array.from({ length: Number(form.places) }, (_, index) => index + 1).join(", ")}).`
            : "One piece per place in each competition, two in doubles." })}</Col>}
        {form.ruleType === "percentOf" && <>
          <Col md={6}>{field("reference", "Percentage of", select,
            options([["", "Choose a product"], ...references.map(product => [product.id, product.name])]))}</Col>
          <Col md={3}>{field("factor", "Factor", { type: "number", min: 0, step: "0.01" }, undefined, { hint: "e.g. 1.25" })}</Col>
        </>}
        {QUANTITY_RULES[form.ruleType] && <Col md={4}>{field("quantity", QUANTITY_RULES[form.ruleType], { type: "number", min: 0, step: "0.1" }, undefined,
          { hint: form.ruleType === "perGroup" && form.family === "balls" ? "Balls of the colour per group." : undefined })}</Col>}
        {GAME_TYPE_RULES.includes(form.ruleType) && <Col md={4}>{field("gameType", "Game type", select,
          options([["", "Singles and doubles"], ...GAME_TYPES.map(type => [type, gameTypeName(type)])]))}</Col>}
      </Row>

      <hr />
      <Check id="product-active" label="Active" checked={form.active} onChange={set("active")}>
        <FormText className="d-block">An inactive product stays on the sheets that have it, but cannot be added to others.</FormText>
      </Check>
      {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    </ModalBody>
    <ModalFooter>
      <Button color="light" disabled={pending} onClick={onClose}>Cancel</Button>
      <Button color="primary" disabled={pending} onClick={onSave}>{pending ? "Checking…" : "Save"}</Button>
    </ModalFooter>
  </Modal>
}
